/**
 * Each game's loading screen, in its own clothes (owner, 2026-10-13: "for each
 * game, it should show the loading stuff from its own unique game theme" — with
 * Brickfront, whose progress bar is a wall laid brick by brick, as the model).
 *
 * Iron Talon and Brickfront are hand-built in components/GameLoader.tsx. Every
 * other game is described HERE and drawn by one renderer there, so a new game
 * gets a themed loader by adding one entry — no new component, no new CSS.
 *
 * What makes one a theme rather than a recolour is `progress`: the thing that
 * fills is something out of the game. Sudoku counts 1 to 9 into nine cells,
 * Fourfall drops discs into the board, Tilefold doubles 2 → 2048, Five Horizons
 * drives a car down the coast road. The colours are the game's own (read from
 * its page), and the stage lines are its world getting ready.
 *
 * The words are flavour, never instructions: the one line that states facts
 * under the bar is the game's own blurb from lib/games.ts, so nothing here can
 * promise a control or a rule the game does not have.
 */

export type LoaderFont = 'block' | 'race' | 'slab' | 'serif' | 'mono' | 'round' | 'neon' | 'hand';

/** Ambient layers. At most one of the particle kinds; any of the overlays. */
export type LoaderFx =
  | 'embers' | 'bubbles' | 'rain' | 'petals' | 'stars' | 'dust'   // particles
  | 'scan' | 'grid' | 'tunnel' | 'paper' | 'sun';                 // overlays

export type CellShape =
  | 'sq' | 'tile' | 'round' | 'coin' | 'diamond' | 'shard' | 'hex' | 'card' | 'shell' | 'flame'
  | 'balloon' | 'eq' | 'stair' | 'tower' | 'rev' | 'window' | 'hole' | 'note' | 'pellet' | 'slash'
  | 'fruit' | 'lantern' | 'glass' | 'crate' | 'pad' | 'git';

export type TrackHead = 'chevron' | 'moon' | 'ship' | 'box' | 'car' | 'bird' | 'coil' | 'ball' | 'link';

export type LoaderProgress =
  /** A bar that fills. */
  | { kind: 'bar'; style: 'stripe' | 'pill' | 'beam' | 'scribble' }
  /** Something that travels from one end to the other. */
  | { kind: 'track'; head: TrackHead; road?: boolean }
  /** Things that are set down one at a time — the Brickfront idea. */
  | {
      kind: 'cells';
      n: number;
      /** Rows the cells are split across (filled bottom row first). Default 1. */
      rows?: number;
      shape: CellShape;
      /** What is written in a cell once it is laid, cycled. */
      glyphs?: readonly string[];
      /** A laid cell's colour, cycled. Default: the theme's `a`. */
      hues?: readonly string[];
      /** Per-cell vertical offset in px, cycled (a melody, a staircase). */
      lift?: readonly number[];
      /** Per-cell height in px, cycled (an equaliser). */
      tall?: readonly number[];
      /** How a cell arrives. */
      anim?: 'drop' | 'pop' | 'flip' | 'slide' | 'rise';
    };

export interface LoaderTheme {
  /** After "TrueMarch Arcade · " over the title: where this game happens. */
  kick: string;
  /** The label on the line under the bar, which carries the game's blurb. */
  tag: string;
  /** What is going on at 0–25, 25–50, 50–72 and 72–100 %. */
  stages: readonly [string, string, string, string];
  /** At 100 %. */
  ready: string;
  /** Background, text, title, and the two accents the progress is drawn in. */
  bg: string;
  ink: string;
  title: string;
  a: string;
  b: string;
  font: LoaderFont;
  /** A daylight game: dark ink on a pale ground. */
  light?: boolean;
  fx?: readonly LoaderFx[];
  progress: LoaderProgress;
}

export const LOADER_THEMES: Record<string, LoaderTheme> = {
  bounty: {
    kick: 'The Ember Company', tag: 'Campaign',
    stages: ['Unfurling the banner', 'Gathering your heroes', 'Charting the battlefield', 'Ready for your next move'],
    ready: 'The company awaits',
    bg: '#111b1e', ink: '#eae6da', title: '#dfbb76', a: '#dfbb76', b: '#6ea78e',
    font: 'serif', fx: ['embers', 'grid'],
    progress: { kind: 'cells', n: 8, rows: 2, shape: 'tile', glyphs: ['⚔', '✦', '➶', '✚'], anim: 'rise' },
  },
  // ── The big games ─────────────────────────────────────────────────────────
  jeep: {
    kick: 'Convoy orders', tag: 'Orders',
    stages: ['Fuelling the jeep', 'Loading the shells', 'Plotting the route', 'Rolling out'],
    ready: 'Engines hot',
    bg: '#120d06', ink: '#f1e6cf', title: '#ffd23f', a: '#ffb14a', b: '#ff3b2a',
    font: 'block', fx: ['dust', 'sun'],
    progress: { kind: 'track', head: 'chevron', road: true },
  },
  brawler: {
    kick: 'The dojo', tag: 'Tonight',
    stages: ['Wrapping your fists', 'Warming up', 'Finding your stance', 'Stepping into the street'],
    ready: 'Fight!',
    bg: '#0b0606', ink: '#f2e4dc', title: '#ffd23f', a: '#e8323c', b: '#3ee6a0',
    font: 'block', fx: ['scan'],
    // A combo counter: twelve hits, one for each mission.
    progress: { kind: 'cells', n: 12, shape: 'slash', hues: ['#e8323c', '#ff6a4a', '#ffd23f'], anim: 'slide' },
  },
  swarmcrypto: {
    kick: 'workspace', tag: 'README',
    stages: ['Resolving dependencies', 'Indexing workspace', 'Syncing the mempool', 'Starting language server'],
    ready: 'Ready',
    bg: '#1f1f1f', ink: '#cccccc', title: '#cccccc', a: '#0078d4', b: '#3794ff',
    font: 'mono',
    progress: { kind: 'bar', style: 'beam' },
  },
  ironlinecrypto: {
    kick: 'source control', tag: 'README',
    stages: ['git fetch origin', 'Resolving deltas', 'Checking out the vault', 'Rebasing strategies'],
    ready: 'HEAD is up to date',
    bg: '#181818', ink: '#cccccc', title: '#cccccc', a: '#0078d4', b: '#2ea043',
    font: 'mono',
    // Commits landing on a branch.
    progress: { kind: 'cells', n: 12, shape: 'git', hues: ['#0078d4', '#2ea043', '#d29922'], anim: 'pop' },
  },
  vf2: {
    kick: 'Fighter select', tag: 'The card',
    stages: ['Opening the arena', 'Calling the roster', 'Taping the ring', 'Round one'],
    ready: 'Fight!',
    bg: '#0a0c12', ink: '#eceef4', title: '#f2b933', a: '#f2b933', b: '#d8422c',
    font: 'race', fx: ['scan'],
    // The roster lighting up: twenty fighters.
    progress: { kind: 'cells', n: 20, rows: 2, shape: 'sq', hues: ['#f2b933', '#d8422c', '#dfe9ff'], anim: 'pop' },
  },
  barrage: {
    kick: 'The armoury', tag: 'The deal',
    stages: ['Opening the crates', 'Dealing the weapons', 'Reading the wind', 'Taking aim'],
    ready: 'Fire at will',
    bg: '#141810', ink: '#e6ead8', title: '#6fd0a8', a: '#6fd0a8', b: '#ff6b5e',
    font: 'slab', fx: ['dust'],
    // Twenty weapons dealt to your side.
    progress: { kind: 'cells', n: 20, rows: 2, shape: 'shell', hues: ['#6fd0a8', '#4ea8ff', '#ff6b5e'], anim: 'rise' },
  },
  emberkeep: {
    kick: 'The keep', tag: 'The oath',
    stages: ['Stoking the forge', 'Lighting the torches', 'Raising the gate', 'Drawing your blade'],
    ready: 'The keep awaits',
    bg: '#130806', ink: '#fff2e0', title: '#ffd27a', a: '#ffb347', b: '#ff6a2a',
    font: 'serif', fx: ['embers', 'sun'],
    // Torches along the wall, lit one by one.
    progress: { kind: 'cells', n: 9, shape: 'flame', hues: ['#ffb347', '#ffd27a', '#ff8a3a'], anim: 'rise' },
  },
  swarm: {
    kick: 'Nightfall', tag: 'The night',
    stages: ['Dusk falls', 'Sharpening the blades', 'The horde stirs', 'Hold until dawn'],
    ready: 'Survive',
    bg: '#16110a', ink: '#e1dfd6', title: '#ffd66b', a: '#c9a24a', b: '#ffd66b',
    font: 'serif', fx: ['dust'],
    // The moon crossing the sky: the night you have to last.
    progress: { kind: 'track', head: 'moon' },
  },
  prism: {
    kick: 'The crystal court', tag: 'The saga',
    stages: ['Cutting the crystals', 'Polishing the lances', 'Aligning the prisms', 'Opening the gates'],
    ready: 'Shine',
    bg: '#1a0638', ink: '#f3e9ff', title: '#ffd86b', a: '#ffd23f', b: '#7dff9a',
    font: 'serif', fx: ['stars'],
    progress: { kind: 'cells', n: 12, shape: 'shard', hues: ['#ffd86b', '#7dff9a', '#8fd0ff', '#ff8fd0', '#c8a2ff'], anim: 'rise' },
  },
  pulse: {
    kick: 'Sound check', tag: 'The duel',
    stages: ['Tuning up', 'Checking the mic', 'Counting in', 'Cue the rival'],
    ready: 'Sing!',
    bg: '#1a0a24', ink: '#ffe9f6', title: '#ffd84d', a: '#ff5fb8', b: '#8ff0ff',
    font: 'round', fx: ['scan'],
    // An equaliser.
    progress: {
      kind: 'cells', n: 18, shape: 'eq', hues: ['#ff5fb8', '#ff8fd0', '#ffd84d', '#8ff0ff'],
      tall: [10, 18, 26, 16, 30, 22, 12, 24, 32, 20, 14, 28, 18, 34, 24, 12, 22, 16], anim: 'rise',
    },
  },
  vortex: {
    kick: 'Tunnel approach', tag: 'The run',
    stages: ['Spinning up', 'Syncing to the beat', 'Charging the phase', 'Entering the tunnel'],
    ready: 'Go',
    bg: '#07030f', ink: '#efe6ff', title: '#ffffff', a: '#ff4fd8', b: '#22e0ff',
    font: 'neon', fx: ['tunnel'],
    progress: { kind: 'bar', style: 'beam' },
  },
  nebula: {
    kick: 'Hangar bay', tag: 'Flight plan',
    stages: ['Fuelling the fighter', 'Arming the cannons', 'Plotting the jump', 'Launching'],
    ready: 'Clear to launch',
    bg: '#060818', ink: '#e6edff', title: '#5ef2ff', a: '#5ef2ff', b: '#ffb347',
    font: 'block', fx: ['stars'],
    progress: { kind: 'track', head: 'ship' },
  },
  depths: {
    kick: 'The descent', tag: 'The delve',
    stages: ['Lighting the lantern', 'Packing the scrolls', 'Rolling a new dungeon', 'Descending'],
    ready: 'Mind the dark',
    bg: '#0d0807', ink: '#f1dfc6', title: '#ffc15a', a: '#c9982e', b: '#ffc15a',
    font: 'serif', fx: ['embers'],
    // Steps going down.
    progress: { kind: 'cells', n: 11, shape: 'stair', lift: [0, 3, 6, 9, 12, 15, 18, 21, 24, 27, 30], anim: 'drop' },
  },
  ironline: {
    kick: 'The war table', tag: 'The tale',
    stages: ['Unrolling the map', 'Mustering the heroes', 'Raising the towers', 'Sounding the horn'],
    ready: 'Hold the line',
    bg: '#1a120a', ink: '#f3e7cf', title: '#ffd23f', a: '#d8a830', b: '#3f8a1c',
    font: 'slab', fx: ['dust', 'sun'],
    // Towers going up along the road.
    progress: { kind: 'cells', n: 10, shape: 'tower', hues: ['#d8a830', '#7f9bc8', '#8a6a44', '#5fa83a'], anim: 'rise' },
  },
  neonrun: {
    kick: 'Dispatch', tag: 'The job',
    stages: ['Scanning the parcel', 'Routing the rooftops', 'Charging the boots', 'Clocking in'],
    ready: 'Deliver',
    bg: '#0a0614', ink: '#f0e8ff', title: '#22e0e0', a: '#ff2d6f', b: '#22e0e0',
    font: 'neon', fx: ['rain', 'grid'],
    progress: { kind: 'track', head: 'box' },
  },
  drift: {
    kick: 'The garage', tag: 'The season',
    stages: ['Warming the tyres', 'Tuning the turbo', 'Rolling to the grid', 'Lights out'],
    ready: 'Send it',
    bg: '#121016', ink: '#f2ecf8', title: '#ffd05a', a: '#ff8a1f', b: '#ffd05a',
    font: 'race', fx: ['scan'],
    // Shift lights: green, amber, red.
    progress: {
      kind: 'cells', n: 15, shape: 'rev', anim: 'pop',
      hues: ['#7dffb0', '#7dffb0', '#7dffb0', '#7dffb0', '#7dffb0', '#ffd23f', '#ffd23f', '#ffd23f', '#ffd23f', '#ffd23f', '#ff5a3a', '#ff5a3a', '#ff5a3a', '#ff5a3a', '#ff5a3a'],
    },
  },
  commando: {
    kick: 'Forward base', tag: 'The mission',
    stages: ['Briefing the squad', 'Loading the belts', 'Fuelling the chopper', 'Dropping in'],
    ready: 'Go go go',
    bg: '#0f140a', ink: '#e4e8cf', title: '#ffd23f', a: '#9be35a', b: '#ff6a4a',
    font: 'block', fx: ['dust'],
    progress: { kind: 'bar', style: 'stripe' },
  },

  // ── Arcade ────────────────────────────────────────────────────────────────
  turbo: {
    kick: 'The coast road', tag: 'The drive',
    stages: ['Topping up the tank', 'Dropping the roof', 'Picking the fork', 'Chasing the sun'],
    ready: 'Go!',
    bg: '#fff3dc', ink: '#1b2a4a', title: '#1b2a4a', a: '#ff9f43', b: '#ff5e5b',
    font: 'race', light: true, fx: ['sun'],
    progress: { kind: 'track', head: 'car', road: true },
  },
  rivercross: {
    kick: 'The kerb', tag: 'The crossing',
    stages: ['Looking left', 'Looking right', 'Counting the lanes', 'Waiting for a gap'],
    ready: 'Hop!',
    bg: '#d2efff', ink: '#24263a', title: '#24263a', a: '#3fae5a', b: '#ff5a5f',
    font: 'round', light: true,
    // Lily pads to hop across.
    progress: { kind: 'cells', n: 10, shape: 'pad', hues: ['#3fae5a', '#5cc46f'], anim: 'pop' },
  },
  balloons: {
    kick: 'Round the world', tag: 'The trip',
    stages: ['Coiling the harpoon', 'Inflating the balloons', 'Stamping the passport', 'Taking aim'],
    ready: 'Pop!',
    bg: '#0e1230', ink: '#eef0ff', title: '#ffd23f', a: '#ff3d7f', b: '#27c7ff',
    font: 'round', fx: ['stars'],
    progress: { kind: 'cells', n: 10, shape: 'balloon', hues: ['#ff3d7f', '#ffd23f', '#7ce040', '#27c7ff', '#bdb8f5'], anim: 'pop' },
  },
  fruit: {
    kick: 'The dojo path', tag: 'The lesson',
    stages: ['Sharpening the blade', 'Washing the fruit', 'Bowing to the master', 'Stepping onto the mat'],
    ready: 'Slice!',
    bg: '#1a0d12', ink: '#fff3e6', title: '#ffd98a', a: '#ff8cbc', b: '#ffd23f',
    font: 'serif', fx: ['petals'],
    // Fruit, each one cut as it lands.
    progress: { kind: 'cells', n: 9, shape: 'fruit', hues: ['#ff5a5f', '#ffb347', '#ffd23f', '#8fd65a', '#ff8cbc'], anim: 'pop' },
  },
  stack: {
    kick: 'The skyline', tag: 'The build',
    stages: ['Pouring the footing', 'Hoisting the floors', 'Squaring the edges', 'Lighting the windows'],
    ready: 'Tap!',
    bg: '#eef3fb', ink: '#1c2440', title: '#1c2440', a: '#ffc93f', b: '#ff6f9c',
    font: 'round', light: true,
    // Fill every window.
    progress: { kind: 'cells', n: 24, rows: 2, shape: 'window', hues: ['#ffc93f', '#ffd97a', '#ff9fbb'], anim: 'pop' },
  },
  splat: {
    kick: 'The ink dock', tag: 'The tide',
    stages: ['Mixing the ink', 'Filling the tanks', 'Charting the isles', 'Diving in'],
    ready: 'Claim it',
    bg: '#2a1038', ink: '#fff4fb', title: '#ffd23f', a: '#ff4fa3', b: '#6ff0ff',
    font: 'round', fx: ['bubbles'],
    progress: { kind: 'bar', style: 'pill' },
  },
  coinrun: {
    kick: 'The world map', tag: 'The quest',
    stages: ['Baking the Muffin', 'Hiding the big coins', 'Stacking the blocks', 'Raising the castle flag'],
    ready: 'Let’s go!',
    bg: '#8fd0ff', ink: '#3b2412', title: '#3b2412', a: '#ffc531', b: '#ff5a3c',
    font: 'round', light: true, fx: ['sun'],
    progress: { kind: 'cells', n: 12, shape: 'coin', hues: ['#ffc531'], anim: 'pop' },
  },
  snake: {
    kick: 'The arena', tag: 'The hunt',
    stages: ['Charging the coil', 'Scattering the orbs', 'Waking the rivals', 'Slithering in'],
    ready: 'Coil up',
    bg: '#04120f', ink: '#e9fff8', title: '#7dffd0', a: '#43f0c4', b: '#ffd84d',
    font: 'neon', fx: ['grid'],
    progress: { kind: 'track', head: 'coil' },
  },
  breaker: {
    kick: 'Glassworks orbit', tag: 'The rack',
    stages: ['Blowing the glass', 'Racking the bricks', 'Charging the paddle', 'Serving the comet'],
    ready: 'Break!',
    bg: '#070a1c', ink: '#eaf2ff', title: '#5ef2ff', a: '#5ef2ff', b: '#ff4fd8',
    font: 'neon', fx: ['stars'],
    progress: { kind: 'cells', n: 20, rows: 2, shape: 'glass', hues: ['#5ef2ff', '#ff4fd8', '#ffd36b', '#a9c3e8'], anim: 'drop' },
  },
  hop: {
    kick: 'The four skies', tag: 'The flight',
    stages: ['Preening the feathers', 'Reading the wind', 'Counting the spires', 'Catching a gust'],
    ready: 'Flap!',
    bg: '#cfeaff', ink: '#1d2340', title: '#1d2340', a: '#ffd23f', b: '#ff6fa8',
    font: 'round', light: true,
    progress: { kind: 'track', head: 'bird' },
  },
  blockfall: {
    kick: 'The lit well', tag: 'The drop',
    stages: ['Sorting the pieces', 'Polishing the T', 'Raising the well', 'Dropping the first piece'],
    ready: 'Clear the row',
    bg: '#0a0820', ink: '#f4f1ff', title: '#ffd56b', a: '#7fe7ff', b: '#ff7ad9',
    font: 'block', fx: ['stars'],
    // Two rows filling, block by block.
    progress: { kind: 'cells', n: 20, rows: 2, shape: 'sq', hues: ['#ffd56b', '#7fe7ff', '#ff7ad9', '#d59bff', '#ff7a9a'], anim: 'drop' },
  },
  maze: {
    kick: 'The neon maze', tag: 'The feast',
    stages: ['Laying the pellets', 'Waking the jellies', 'Lighting the corridors', 'Opening the gate'],
    ready: 'Gobble!',
    bg: '#05060f', ink: '#e6faff', title: '#ffd84d', a: '#3ee8ff', b: '#ffd84d',
    font: 'neon', fx: ['scan'],
    // A row of pellets being eaten.
    progress: { kind: 'cells', n: 18, shape: 'pellet' },
  },
  defender: {
    kick: 'Hive sector', tag: 'The sortie',
    stages: ['Scrambling the wing', 'Charging the lasers', 'Scanning the hive', 'Diving in'],
    ready: 'Fly double',
    bg: '#05070f', ink: '#f2ecda', title: '#ffd36b', a: '#ffd36b', b: '#5ff0d8',
    font: 'block', fx: ['stars'],
    progress: { kind: 'cells', n: 14, shape: 'hex', hues: ['#ffd36b', '#e2b84a', '#5ff0d8'], anim: 'pop' },
  },
  skyclimb: {
    kick: 'Page one', tag: 'The doodle',
    stages: ['Sharpening the pencil', 'Sketching the platforms', 'Doodling the monsters', 'Inking the summit'],
    ready: 'Jump!',
    bg: '#fffdf4', ink: '#22305a', title: '#22305a', a: '#ff6f91', b: '#ffe066',
    font: 'hand', light: true, fx: ['paper'],
    progress: { kind: 'bar', style: 'scribble' },
  },
  moles: {
    kick: 'The garden', tag: 'The lawn',
    stages: ['Mowing the lawn', 'Counting the burrows', 'Polishing the mallet', 'Listening for digging'],
    ready: 'Bonk!',
    bg: '#f6ead2', ink: '#3a2414', title: '#3a2414', a: '#8a5a34', b: '#e2427a',
    font: 'hand', light: true,
    // Moles coming up, hole by hole.
    progress: { kind: 'cells', n: 9, shape: 'hole', hues: ['#8a5a34', '#a06a3e'], anim: 'rise' },
  },

  // ── Puzzle ────────────────────────────────────────────────────────────────
  bubble: {
    kick: 'The lantern festival', tag: 'Tonight',
    stages: ['Stringing the lanterns', 'Lighting the wicks', 'Loading the launcher', 'Aiming high'],
    ready: 'Pop three',
    bg: '#1a1030', ink: '#fff6dc', title: '#ffc857', a: '#ffc857', b: '#7ff0c8',
    font: 'serif', fx: ['embers'],
    progress: { kind: 'cells', n: 10, shape: 'lantern', hues: ['#ffc857', '#ff8fa3', '#7ff0c8', '#d2c0ea'], anim: 'rise' },
  },
  flow: {
    kick: 'The switchboard', tag: 'The district',
    stages: ['Laying the grid', 'Placing the endpoints', 'Checking for one answer', 'Powering the district'],
    ready: 'Connect',
    bg: '#17182a', ink: '#eeeefc', title: '#ffffff', a: '#2ad4c4', b: '#7c5cff',
    font: 'neon', fx: ['grid'],
    // One line, joining two endpoints.
    progress: { kind: 'track', head: 'link' },
  },
  crates: {
    kick: 'The loading bay', tag: 'The shift',
    stages: ['Clocking in', 'Stacking the pallets', 'Waking the foreman drone', 'Opening the shutters'],
    ready: 'Push, never pull',
    bg: '#23201c', ink: '#efe6d6', title: '#f0a020', a: '#f0a020', b: '#e8562a',
    font: 'slab', fx: ['dust'],
    progress: { kind: 'cells', n: 11, shape: 'crate', hues: ['#c98a3a', '#b9772c', '#d79a48'], anim: 'slide' },
  },
  2048: {
    kick: 'The voyage', tag: 'The chart',
    stages: ['Shuffling the tiles', 'Folding the twos', 'Charting the voyage', 'Sliding off'],
    ready: 'Fold!',
    bg: '#2b2620', ink: '#f5ecdc', title: '#f2683c', a: '#d79a1c', b: '#f2683c',
    font: 'round',
    // The doubling itself.
    progress: {
      kind: 'cells', n: 11, shape: 'tile', anim: 'pop',
      glyphs: ['2', '4', '8', '16', '32', '64', '128', '256', '512', '1024', '2048'],
      hues: ['#eee4da', '#ede0c8', '#f2b179', '#f59563', '#f67c5f', '#f65e3b', '#edcf72', '#edcc61', '#edc850', '#edc53f', '#edc22e'],
    },
  },
  minesweeper: {
    kick: 'The dig site', tag: 'The expedition',
    stages: ['Unpacking the brushes', 'Mapping the ruins', 'Flagging the traps', 'Lifting the first slab'],
    ready: 'Dig carefully',
    bg: '#1c130a', ink: '#f6e6c4', title: '#ffd66b', a: '#e0b04a', b: '#b3261e',
    font: 'slab', fx: ['dust'],
    // Slabs turned over, numbers underneath.
    progress: {
      kind: 'cells', n: 12, shape: 'tile', anim: 'flip',
      glyphs: ['1', '2', '1', '', '3', '1', '', '2', '2', '1', '', '1'],
      hues: ['#e9d3a0', '#e2c88e'],
    },
  },
  memory: {
    kick: 'The album', tag: 'The page',
    stages: ['Pasting the photos', 'Shuffling the cards', 'Turning them face down', 'Opening page one'],
    ready: 'Find the pair',
    bg: '#2a2230', ink: '#f8eef5', title: '#e5a521', a: '#e0457b', b: '#e5a521',
    font: 'round',
    // Cards flipping face up — in pairs.
    progress: {
      kind: 'cells', n: 10, shape: 'card', anim: 'flip',
      glyphs: ['★', '♥', '★', '♦', '♥', '♣', '♦', '♠', '♣', '♠'],
      hues: ['#e5a521', '#e0457b', '#e5a521', '#2f9e5b', '#e0457b', '#5b6cd9', '#2f9e5b', '#75697c', '#5b6cd9', '#75697c'],
    },
  },
  gems: {
    kick: 'The duelling board', tag: 'The duel',
    stages: ['Cutting the gems', 'Polishing the skulls', 'Charging the spells', 'Drawing lots'],
    ready: 'Match three',
    bg: '#2a2118', ink: '#f4e9d8', title: '#e0a21a', a: '#e0a21a', b: '#c0392b',
    font: 'serif', fx: ['dust'],
    progress: { kind: 'cells', n: 11, shape: 'diamond', hues: ['#c0392b', '#e0a21a', '#2f9e5b', '#3b82d6', '#9b59b6'], anim: 'drop' },
  },
  sudoku: {
    kick: 'The garden', tag: 'The path',
    stages: ['Raking the gravel', 'Setting the stones', 'Counting to nine', 'Pouring the tea'],
    ready: 'Begin',
    bg: '#f7f0e1', ink: '#2a2521', title: '#2a2521', a: '#c8412b', b: '#b8862d',
    font: 'serif', light: true, fx: ['petals'],
    // One to nine, each in its cell.
    progress: {
      kind: 'cells', n: 9, shape: 'tile', anim: 'pop',
      glyphs: ['1', '2', '3', '4', '5', '6', '7', '8', '9'],
      hues: ['#efe4cd'],
    },
  },
  echo: {
    kick: 'The songbook', tag: 'The song',
    stages: ['Lighting the lanterns', 'Tuning the chimes', 'Humming the first bar', 'Listening'],
    ready: 'Your turn',
    bg: '#05040c', ink: '#ffe7b0', title: '#ffd77d', a: '#ffd77d', b: '#8ff5e0',
    font: 'serif', fx: ['embers'],
    // A melody, written out note by note.
    progress: {
      kind: 'cells', n: 10, shape: 'note', anim: 'pop',
      lift: [0, -6, -12, -6, 0, -18, -12, -6, -12, 0],
      hues: ['#ffd77d', '#8ff5e0'],
    },
  },

  // ── Board ─────────────────────────────────────────────────────────────────
  tictactoe: {
    kick: 'The grid', tag: 'The ladder',
    stages: ['Drawing the grid', 'Lighting the neon', 'Waking the rivals', 'Your move'],
    ready: 'Three in a row',
    bg: '#160a2a', ink: '#f3eaff', title: '#ffffff', a: '#e0218a', b: '#ffc93f',
    font: 'neon', fx: ['scan'],
    progress: {
      kind: 'cells', n: 9, shape: 'tile', anim: 'pop',
      glyphs: ['✕', '○', '✕', '○', '✕', '○', '✕', '○', '✕'],
      hues: ['#e0218a', '#ffc93f'],
    },
  },
  solitaire: {
    kick: 'The parlour', tag: 'The table',
    stages: ['Brushing the velvet', 'Shuffling the deck', 'Cutting the cards', 'Dealing'],
    ready: 'Your deal',
    bg: '#2a0a16', ink: '#f3dfc0', title: '#f2cf7a', a: '#f2cf7a', b: '#5ad0ff',
    font: 'serif',
    // Seven columns, dealt.
    progress: {
      kind: 'cells', n: 7, shape: 'card', anim: 'slide',
      glyphs: ['A♠', 'K♥', 'Q♣', 'J♦', '10♠', '9♥', '8♣'],
      hues: ['#1c1c22', '#c8323c'],
    },
  },
  fourinarow: {
    kick: 'The league', tag: 'The season',
    stages: ['Racking the discs', 'Standing the board', 'Tossing the coin', 'First drop'],
    ready: 'Line up four',
    bg: '#18233a', ink: '#eef2fa', title: '#f2b221', a: '#e8455a', b: '#f2b221',
    font: 'round',
    // Discs dropping into the bottom row, turn by turn.
    progress: { kind: 'cells', n: 7, shape: 'round', hues: ['#e8455a', '#f2b221'], anim: 'drop' },
  },
  paddle: {
    kick: 'The venue', tag: 'The circuit',
    stages: ['Chalking the table', 'Stringing the net', 'Warming up the wrist', 'Serving'],
    ready: 'Play!',
    bg: '#0a1418', ink: '#e8f6f3', title: '#ffd36b', a: '#5ef2c8', b: '#ff8a3d',
    font: 'race', fx: ['scan'],
    // A rally: the ball between two bats.
    progress: { kind: 'track', head: 'ball' },
  },
  // ── Added 2026-10-13: the five single-player games from the owner's list ──
  castle: {
    kick: 'The gate tower', tag: 'The siege',
    stages: ['Stringing the bow', 'Counting the arrows', 'Dropping the portcullis', 'They are at the treeline'],
    ready: 'Loose!',
    bg: '#0e1626', ink: '#e6ecf5', title: '#f2c14e', a: '#2f6fd0', b: '#f2c14e',
    font: 'slab', fx: ['stars'],
    // The wall going up, merlon by merlon.
    progress: { kind: 'cells', n: 10, shape: 'tower', hues: ['#2f6fd0', '#8fb4e8', '#f2c14e'], anim: 'rise' },
  },
  bakery: {
    kick: "Poppy's bakery", tag: "Today's bake",
    stages: ['Lighting the ovens', 'Kneading the first batch', 'Proving the dough', 'Turning the sign to OPEN'],
    ready: 'Open!',
    bg: '#f6ead2', ink: '#4a2f1c', title: '#c8452f', a: '#c8452f', b: '#e0a030',
    font: 'round', light: true, fx: ['paper'],
    // A tray of loaves coming out of the oven, one at a time.
    progress: { kind: 'cells', n: 8, shape: 'round', hues: ['#e0a030', '#c98a2b', '#a8641f'], anim: 'pop' },
  },
  sheepwar: {
    kick: 'Thistle Fell', tag: 'The shove',
    stages: ['Counting the flock', 'Raking the droves', 'Stacking the hay', 'Ringing the bell'],
    ready: 'Shove!',
    bg: '#dfeecb', ink: '#2c3a1e', title: '#3f7d2a', a: '#6fb84a', b: '#d8452f',
    font: 'block', light: true,
    // Bales stacked on the rick.
    progress: { kind: 'cells', n: 12, rows: 2, shape: 'crate', hues: ['#e0b83a', '#c99a2b'], anim: 'drop' },
  },
  bubblewar: {
    kick: 'The tide pool', tag: 'This tide',
    stages: ['Filling the pool', 'Scattering the specks', 'Waking the big ones', 'The tide is coming in'],
    ready: 'Gulp!',
    bg: '#10244a', ink: '#e4f1f5', title: '#ffd66b', a: '#1f8fa3', b: '#ff5a4e',
    font: 'round', fx: ['bubbles'],
    progress: { kind: 'cells', n: 9, shape: 'round', hues: ['#7fd6e0', '#ffd66b', '#ff8fa3', '#8fe08a'], anim: 'pop' },
  },
  prank: {
    kick: 'Mischief Row', tag: "Tonight's bill",
    stages: ['Raising the curtain', 'Filling the bucket', 'Oiling the hinges', 'He is putting the kettle on'],
    ready: 'Tiptoe!',
    bg: '#161a2b', ink: '#f1e6cf', title: '#ffcd75', a: '#b13e53', b: '#ffcd75',
    font: 'slab',
    // The windows of No. 9 lighting up, one room at a time.
    progress: { kind: 'cells', n: 8, rows: 2, shape: 'window', hues: ['#ffcd75', '#f4a43a'], anim: 'pop' },
  },
};
